import { reconcileGiftAddons, assertGiftAddonsComplete } from './vspots-gift-cart-state.js?v=20260916-2'

const giftPropertyKeys = [
  '_vspots_gift',
  '_vspots_gift_token',
  '_vspots_gift_layout',
  '_vspots_gift_recipient',
  '_vspots_gift_message',
  'Als Geschenk',
  'Geschenkdesign',
  'Für',
  'Widmung',
]

const randomToken = () =>
  globalThis.crypto?.randomUUID?.() ?? 'gift-' + Date.now() + '-' + Math.random().toString(36).slice(2)

const cartPath = (path) => {
  const root = globalThis.Shopify?.routes?.root || '/'
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`
  return `${normalizedRoot}${path.replace(/^\/+/, '')}`
}

const cartRequest = async (path, body) => {
  const response = await fetch(path, {
    body: JSON.stringify(body),
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    method: 'POST',
  })
  if (!response.ok) {
    const details = await response.json().catch(() => null)
    throw new Error(details?.description ?? 'Der Warenkorb konnte nicht aktualisiert werden.')
  }
  return response.json()
}

const previewCopy = {
  danke: ['EIN DANKESCHÖN', 'Danke!'],
  'frohe-weihnachten': ['FÜR DEINE LIEBSTEN', 'Frohe Weihnachten'],
  'happy-birthday': ['FÜR DICH', 'Happy Birthday'],
  'in-liebe': ['VON HERZEN', 'In Liebe'],
  'premium-noir': ['PREMIUM GUTSCHEIN', 'Für dich'],
  standard: ['VSPOTS GUTSCHEIN', 'Dein Erlebnis wartet'],
}

const syncPreview = (editor) => {
  const preview = editor.querySelector('[data-vspots-gift-preview]')
  const design = editor.querySelector('input[type="radio"]:checked')
  if (!(preview instanceof HTMLElement) || !(design instanceof HTMLInputElement)) return

  const [kicker, title] = previewCopy[design.value] ?? previewCopy.standard
  const kickerElement = preview.querySelector('[data-vspots-gift-preview-kicker]')
  const titleElement = preview.querySelector('[data-vspots-gift-preview-title]')
  const recipientElement = preview.querySelector('[data-vspots-gift-preview-recipient]')
  const messageElement = preview.querySelector('[data-vspots-gift-preview-message]')
  const recipient = editor.querySelector('[data-vspots-gift-recipient]')
  const message = editor.querySelector('[data-vspots-gift-message]')
  const recipientValue = recipient instanceof HTMLInputElement ? recipient.value.trim() : ''
  const messageValue = message instanceof HTMLTextAreaElement ? message.value.trim() : ''

  preview.dataset.layout = design.value
  if (kickerElement instanceof HTMLElement) kickerElement.textContent = kicker
  if (titleElement instanceof HTMLElement) titleElement.textContent = title
  if (recipientElement instanceof HTMLElement) {
    recipientElement.textContent = recipientValue === '' ? 'Dein Erlebnis-Gutschein' : `Für ${recipientValue}`
  }
  if (messageElement instanceof HTMLElement) {
    messageElement.textContent = messageValue
    messageElement.hidden = messageValue === ''
  }
}

const propertiesFrom = (editor) => {
  try {
    const parsed = JSON.parse(editor.dataset.properties ?? '{}')
    return Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed
  } catch {
    return {}
  }
}

const findAddOn = (token) =>
  [...document.querySelectorAll('[data-vspots-gift-addon="true"]')].find(
    (line) => line instanceof HTMLElement && line.dataset.vspotsGiftToken === token,
  )

const setBusy = (editor, busy) => {
  editor.querySelectorAll('button, input, textarea').forEach((control) => {
    if (
      control instanceof HTMLButtonElement
      || control instanceof HTMLInputElement
      || control instanceof HTMLTextAreaElement
    ) {
      control.disabled = busy || control.dataset.giftUnavailable === 'true'
    }
  })
}

const syncAcceleratedCheckout = () => {
  const hasUnsavedGiftDetails = document.querySelector('[data-vspots-gift-editor][data-gift-dirty="true"]') !== null
  document.querySelectorAll('.additional-checkout-buttons').forEach((buttons) => {
    if (buttons instanceof HTMLElement) buttons.hidden = hasUnsavedGiftDetails
  })
}

const markDirty = (editor) => {
  editor.dataset.giftDirty = 'true'
  syncAcceleratedCheckout()
}

const saveEditor = async (editor, { reload = true } = {}) => {
  if (editor.dataset.giftSaving === 'true') return false
  const toggle = editor.querySelector('[data-vspots-gift-toggle]')
  const save = editor.querySelector('[data-vspots-gift-save]')
  const error = editor.querySelector('[data-vspots-gift-error]')
  if (!(toggle instanceof HTMLInputElement) || !(save instanceof HTMLButtonElement)) return false

  const properties = propertiesFrom(editor)
  const oldToken = editor.dataset.giftToken ?? ''
  const existingAddOn = oldToken === '' ? null : findAddOn(oldToken)
  giftPropertyKeys.forEach((key) => delete properties[key])

  editor.dataset.giftSaving = 'true'
  setBusy(editor, true)
  save.textContent = 'Wird gespeichert …'
  if (error instanceof HTMLElement) error.hidden = true

  try {
    const selectedDesign = editor.querySelector('input[type="radio"]:checked')
    if (toggle.checked && selectedDesign?.value !== 'standard' && !selectedDesign?.dataset.variantId) {
      throw new Error('Dieses Geschenkdesign ist gerade nicht verfügbar. Bitte wähle das kostenlose Standarddesign.')
    }
    if (existingAddOn instanceof HTMLElement) {
      await cartRequest(cartPath('cart/change.js'), {
        id: existingAddOn.dataset.lineKey,
        quantity: 0,
      })
    }

    if (toggle.checked) {
      const design = editor.querySelector('input[type="radio"]:checked')
      const recipient = editor.querySelector('[data-vspots-gift-recipient]')
      const message = editor.querySelector('[data-vspots-gift-message]')
      if (!(design instanceof HTMLInputElement)) throw new Error('Bitte wähle ein Gutschein-Design.')
      const token = oldToken || randomToken()
      const recipientValue = recipient instanceof HTMLInputElement ? recipient.value.trim().slice(0, 80) : ''
      const messageValue = message instanceof HTMLTextAreaElement ? message.value.trim().slice(0, 250) : ''

      Object.assign(properties, {
        _vspots_gift: 'true',
        _vspots_gift_layout: design.value,
        _vspots_gift_token: token,
        'Als Geschenk': 'Ja',
        Geschenkdesign: design.dataset.name ?? design.value,
      })
      if (recipientValue !== '') {
        properties._vspots_gift_recipient = recipientValue
        properties.Für = recipientValue
      }
      if (messageValue !== '') {
        properties._vspots_gift_message = messageValue
        properties.Widmung = messageValue
      }

      await cartRequest(cartPath('cart/change.js'), {
        id: editor.dataset.lineKey,
        properties,
        quantity: Number.parseInt(editor.dataset.lineQuantity ?? '1', 10),
      })

      if (design.value !== 'standard' && design.dataset.variantId) {
        await cartRequest(cartPath('cart/add.js'), {
          items: [
            {
              id: Number.parseInt(design.dataset.variantId, 10),
              properties: {
                _vspots_gift_addon: 'true',
                _vspots_gift_layout: design.value,
                _vspots_gift_parent_token: token,
              },
              quantity: Number.parseInt(editor.dataset.lineQuantity ?? '1', 10),
            },
          ],
        })
      }
    } else {
      // Shopify keeps existing properties for an empty object; an explicit false
      // marker clears the gift metadata while retaining unrelated properties.
      properties._vspots_gift = 'false'
      await cartRequest(cartPath('cart/change.js'), {
        id: editor.dataset.lineKey,
        properties,
        quantity: Number.parseInt(editor.dataset.lineQuantity ?? '1', 10),
      })
    }

    editor.dataset.giftDirty = 'false'
    syncAcceleratedCheckout()
    if (reload) {
      const drawer = editor.closest('theme-drawer[open]')
      if (drawer?.id) {
        try { sessionStorage.setItem('vspots-gift-reopen-drawer', drawer.id) } catch {}
      }
      window.location.reload()
    }
    return true
  } catch (requestError) {
    setBusy(editor, false)
    save.textContent = 'Geschenkdetails speichern'
    if (error instanceof HTMLElement) {
      const message = requestError instanceof Error ? requestError.message : ''
      error.textContent = /expected pattern|invalid url/i.test(message)
        ? 'Die Geschenkdetails konnten nicht gespeichert werden. Bitte versuche es erneut.'
        : message || 'Bitte versuche es erneut.'
      error.hidden = false
    }
    return false
  } finally {
    delete editor.dataset.giftSaving
  }
}

document.addEventListener('change', (event) => {
  const target = event.target
  if (!(target instanceof Element)) return
  const editor = target.closest('[data-vspots-gift-editor]')
  if (!(editor instanceof HTMLElement)) return
  if (target.matches('[data-vspots-gift-toggle]') && target instanceof HTMLInputElement) {
    editor.classList.toggle('is-active', target.checked)
    syncPreview(editor)
    if (!target.checked && propertiesFrom(editor)._vspots_gift === 'true') {
      markDirty(editor)
      // Turning the gift off must persist immediately: its save button is now hidden.
      void saveEditor(editor).then((saved) => {
        if (!saved) {
          target.checked = true
          editor.classList.add('is-active')
        }
      })
      return
    }
  }
  if (target.matches('.vspots-gift-design input[type="radio"]')) {
    syncPreview(editor)
  }
  markDirty(editor)
})

document.addEventListener('input', (event) => {
  const target = event.target
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return
  const editor = target.closest('[data-vspots-gift-editor]')
  if (!(editor instanceof HTMLElement)) return
  if (target.matches('[data-vspots-gift-message]')) {
    const counter = editor.querySelector('[data-vspots-gift-count]')
    if (counter instanceof HTMLElement) counter.textContent = String(target.value.length)
  }
  if (target.matches('[data-vspots-gift-recipient], [data-vspots-gift-message]')) syncPreview(editor)
  markDirty(editor)
})

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-vspots-gift-save]') : null
  const editor = target?.closest('[data-vspots-gift-editor]')
  if (editor instanceof HTMLElement) void saveEditor(editor)
})

document.querySelectorAll('[data-vspots-gift-editor]').forEach((editor) => {
  if (editor instanceof HTMLElement) syncPreview(editor)
})
syncAcceleratedCheckout()

// Keep the updated cart visible on mobile after persisting gift details.
try {
  const drawerId = sessionStorage.getItem('vspots-gift-reopen-drawer')
  sessionStorage.removeItem('vspots-gift-reopen-drawer')
  if (drawerId) {
    customElements.whenDefined('theme-drawer').then(() => {
      document.getElementById(drawerId)?.open?.()
    })
  }
} catch {}

const repairOrphanedAddons = async () => {
  const tokens = new Set([...document.querySelectorAll('[data-vspots-gift-editor]')].map((editor) => editor.dataset.giftToken))
  const orphan = [...document.querySelectorAll('[data-vspots-gift-addon="true"]')].some((line) => !tokens.has(line.dataset.vspotsGiftToken))
  if (!orphan) return
  const response = await fetch(cartPath('cart.js'), { headers: { Accept: 'application/json' } })
  if (!response.ok) return
  const cart = await response.json()
  const updated = await reconcileGiftAddons(cart)
  if (updated !== cart) window.location.reload()
}

void repairOrphanedAddons().catch(() => {})

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target.closest('[name="checkout"]') : null
    if (!(target instanceof HTMLButtonElement) || target.dataset.vspotsGiftProceeding === 'true') return

    const dirtyEditors = [...document.querySelectorAll('[data-vspots-gift-editor][data-gift-dirty="true"]')].filter(
      (editor) => editor instanceof HTMLElement,
    )
    const hasGiftAddons = document.querySelector('[data-vspots-gift-addon="true"]') !== null
    const hasPaidGift = [...document.querySelectorAll('[data-vspots-gift-editor]')].some((editor) => {
      const p = propertiesFrom(editor)
      return p._vspots_gift === 'true' && p._vspots_gift_layout && p._vspots_gift_layout !== 'standard'
    })
    if (dirtyEditors.length === 0 && !hasGiftAddons && !hasPaidGift) return

    event.preventDefault()
    event.stopImmediatePropagation()
    target.disabled = true
    target.setAttribute('aria-busy', 'true')

    void (async () => {
      try {
        for (const editor of dirtyEditors) {
          if (!(editor instanceof HTMLElement) || !(await saveEditor(editor, { reload: false }))) {
            target.disabled = false
            target.removeAttribute('aria-busy')
            return
          }
        }
        const response = await fetch(cartPath('cart.js'), { headers: { Accept: 'application/json' } })
        if (!response.ok) throw new Error('Der Warenkorb konnte nicht geprüft werden. Bitte versuche es erneut.')
        const cart = await reconcileGiftAddons(await response.json())
        assertGiftAddonsComplete(cart.items)
        if (!cart.items?.length) {
          window.location.assign(cartPath('cart'))
          return
        }
        target.dataset.vspotsGiftProceeding = 'true'
        window.location.assign(cartPath('checkout'))
      } catch (error) {
        target.disabled = false
        target.removeAttribute('aria-busy')
        let status = target.parentElement.querySelector('[data-vspots-gift-checkout-error]')
        if (!status) {
          status = document.createElement('p')
          status.dataset.vspotsGiftCheckoutError = ''
          status.setAttribute('role', 'alert')
          target.before(status)
        }
        status.textContent = error instanceof Error ? error.message : 'Bitte lade den Warenkorb erneut.'
      }
    })()
  },
  true,
)
